import { useTranslation } from 'next-i18next'

import { useDispatch } from 'react-redux'

import { useRouter } from 'next/router'

import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { CurriculumCertificate } from '~/curriculum/types/curriculum.types'

import ROUTES from '~/shared/constants/routes.const'
import { setAchievementPagePreviousUrl } from '~/redux/slices/dashboard'
import Button from '~/components/Buttons/Button'
import useHasLegacyAchievements from '~/achievements/hooks/useHasLegacyAchievements'
import { Breakpoint, useCheckBreakpoint } from '~/utils'

export default function MasteryCertificates({
  certificates,
  largeBadge = false,
}: {
  certificates?: CurriculumCertificate[]
  largeBadge?: boolean
}): React.ReactElement {
  const { t } = useTranslation('curriculum')
  const latestCertificate = certificates && certificates.length > 0 ? certificates[0] : undefined
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.MD })
  const dispatch = useDispatch()
  const router = useRouter()
  const { hasAny: hasLegacyAchievements, isLoading: isLoadingLegacy } = useHasLegacyAchievements()
  // The page is worth opening for a legacy badge too, not just a certificate. `certificates` is undefined
  // until its query lands, so stay open while anything is still loading rather than locking the button and
  // unlocking it a moment later.
  const isLoading = certificates === undefined || isLoadingLegacy
  const hasSomethingToShow = isLoading || Boolean(certificates?.length) || hasLegacyAchievements

  return (
    <div className="w-full items-center text-center h-full">
      <div className={`flex flex-col py-2 gap-1.5 md:gap-0 h-full ${largeBadge ? 'md:py-4' : ''}`}>
        <h2 className="flex justify-center items-center text-white text-sm font-bold mx-1 leading-5">
          {isMobile
            ? t('curriculum:mappingQuiz.masteryCertificate')
            : t('curriculum:mappingQuiz.latestMasteryCertificate')}
        </h2>

        <div className="flex flex-col flex-1 justify-center items-center gap-2">
          <div
            className={hasSomethingToShow ? 'cursor-pointer' : ''}
            key={`certificate-${latestCertificate?.gradeLevel}-${latestCertificate?.date}`}
            onClick={() => {
              if (!hasSomethingToShow) return
              dispatch(setAchievementPagePreviousUrl(router.asPath))
              router.push(ROUTES.ACHIEVEMENTS)
            }}
          >
            <ResponsiveImage
              alt="Certificate"
              height={1080}
              imageClassName="w-16 md:w-25 lg:w-21"
              srcKey={
                certificates && certificates.length > 0
                  ? `curriculum/certificate/mastery-certificate-${latestCertificate?.topicId}_v4.png`
                  : 'dashboard/no-certificate-icon_v1.png'
              }
              width={1080}
            />
          </div>
          <p className="text-white text-xs font-semibold text-center whitespace-normal leading-tight mx-2 -mt-1">
            {latestCertificate ? latestCertificate.curriculumSubject : t('notEarnedYet')}
          </p>
        </div>
        <Button
          className="bg-purple mx-auto h-9 w-9/10 shadow-purple-drop-glow !rounded-full font-bold text-xs"
          disabled={!hasSomethingToShow}
          href={ROUTES.ACHIEVEMENTS}
          onClick={() => {
            dispatch(setAchievementPagePreviousUrl(router.asPath))
          }}
        >
          {t('curriculum:mappingQuiz.seeAll')}
        </Button>
      </div>
    </div>
  )
}
